/*
 * Messages between the home and the pharmacy.
 *
 * The same screen on both sides. It replaces the phone call that nobody wrote down: the answer
 * ends up attached to the resident it was about, and both sides can find it a month later.
 *
 * It is not for an emergency. The screen says so, because somebody will otherwise type a message
 * about a missing insulin and sit waiting.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from './api.ts';
import type { Resident, User } from './api.ts';
import { Overlay } from './overlay.tsx';

type Thread = {
  id: string;
  subject: string;
  aboutType: string | null;
  aboutId: string | null;
  aboutName: string;
  urgent: boolean;
  createdAt: string;
  lastAt: string;
  closed: boolean;
  messages: number;
  unread: number;
  lastBody: string;
  lastSide: string;
  lastBy: string;
};

type Message = {
  id: string;
  body: string;
  side: string;
  sentAt: string;
  sentBy: string;
  mine: boolean;
};

export function MessagesScreen({ homeId, user }: { homeId: string; user: User }) {
  const [show, setShow] = useState<'open' | 'all'>('open');
  const [threads, setThreads] = useState<Thread[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ threads: Thread[] }>(`/api/homes/${homeId}/threads?show=${show}`);
      setThreads(data.threads);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the messages.');
    }
  }, [homeId, show]);

  useEffect(() => {
    void load();
  }, [load]);

  // The other side may answer while this is open, so look again every half a minute.
  useEffect(() => {
    const timer = window.setInterval(() => void load(), 30_000);
    return () => window.clearInterval(timer);
  }, [load]);

  const theOther = user.side === 'pharmacy' ? 'the home' : 'the pharmacy';

  return (
    <section>
      <div className="head">
        <h1>Messages</h1>
        <div className="row-actions">
          <button type="button" onClick={() => setStarting(true)}>Start one</button>
          <button type="button" className="quiet" onClick={() => void load()}>Refresh</button>
        </div>
      </div>
      <p className="muted">
        For anything that can wait: a query about a dose, a missing item, a question for {theOther}.
        Ring for anything urgent — nobody is watching this screen all day.
      </p>

      <nav className="tabs">
        <button type="button" className={show === 'open' ? 'on' : ''} onClick={() => setShow('open')}>Open</button>
        <button type="button" className={show === 'all' ? 'on' : ''} onClick={() => setShow('all')}>Everything</button>
      </nav>

      {error ? <p className="error" role="alert">{error}</p> : null}
      {threads && !threads.length ? (
        <p className="notice">{show === 'open' ? 'Nothing waiting for an answer.' : 'No messages yet.'}</p>
      ) : null}

      <ul className="list">
        {(threads ?? []).map((thread) => (
          <li key={thread.id}>
            <button type="button" className="row column" onClick={() => setOpenId(thread.id)}>
              <div className="row-line">
                <span className="row-main">{thread.subject}</span>
                <span className="chips">
                  {thread.urgent && !thread.closed ? <span className="chip warn">Urgent</span> : null}
                  {thread.unread ? <span className="chip unread">{thread.unread} new</span> : null}
                  {thread.closed ? <span className="chip done">Closed</span> : null}
                </span>
              </div>
              {thread.aboutName ? <p className="muted small">About {thread.aboutName}</p> : null}
              <p className="muted small">
                <strong>{thread.lastSide === user.side ? 'You' : thread.lastBy}</strong>
                {' · '}
                {formatWhen(thread.lastAt)}
                {' · '}
                {thread.lastBody.length > 90 ? `${thread.lastBody.slice(0, 90)}…` : thread.lastBody}
              </p>
            </button>
          </li>
        ))}
      </ul>

      {openId ? (
        <ThreadDialog
          threadId={openId}
          user={user}
          onClose={() => {
            setOpenId(null);
            void load();
          }}
        />
      ) : null}

      {starting ? (
        <StartDialog
          homeId={homeId}
          onClose={() => setStarting(false)}
          onStarted={(id) => {
            setStarting(false);
            setShow('open');
            setOpenId(id);
            void load();
          }}
        />
      ) : null}
    </section>
  );
}

function ThreadDialog({ threadId, user, onClose }: { threadId: string; user: User; onClose: () => void }) {
  const [thread, setThread] = useState<Thread | null>(null);
  const [messages, setMessages] = useState<Message[] | null>(null);
  const [reply, setReply] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const foot = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ thread: Thread; messages: Message[] }>(`/api/threads/${threadId}`);
      setThread(data.thread);
      setMessages(data.messages);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not open that message.');
    }
  }, [threadId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    foot.current?.scrollIntoView({ block: 'nearest' });
  }, [messages]);

  const send = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setBusy(true);
      setError('');
      try {
        await api.post(`/api/threads/${threadId}/reply`, { message: reply });
        setReply('');
        await load();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That did not send.');
      } finally {
        setBusy(false);
      }
    },
    [threadId, reply, load],
  );

  const close = useCallback(async () => {
    setBusy(true);
    try {
      await api.post(`/api/threads/${threadId}/close`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not save.');
    } finally {
      setBusy(false);
    }
  }, [threadId, load]);

  return (
    <Overlay onClose={onClose}>
      <div className="card wide">
        <div className="head">
          <div>
            <h2>{thread?.subject ?? 'Message'}</h2>
            {thread?.aboutName ? <p className="muted small">About {thread.aboutName}</p> : null}
          </div>
          <button type="button" className="quiet" onClick={onClose}>Close</button>
        </div>

        {thread?.closed ? <p className="notice">This conversation has been marked as dealt with.</p> : null}
        {error ? <p className="error" role="alert">{error}</p> : null}

        <div className="thread">
          {(messages ?? []).map((message) => (
            <div key={message.id} className={`bubble ${message.mine ? 'mine' : 'theirs'}`}>
              <p className="muted small">
                {message.sentBy} · {message.side === 'pharmacy' ? 'Pharmacy' : 'Home'} · {formatWhen(message.sentAt)}
              </p>
              <p>{message.body}</p>
            </div>
          ))}
          <div ref={foot} />
        </div>

        {!thread?.closed ? (
          <form onSubmit={send}>
            <label htmlFor="reply">Your reply</label>
            <textarea id="reply" rows={3} required value={reply} onChange={(e) => setReply(e.target.value)} />
            <div className="row-actions">
              <button type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send'}</button>
              <button type="button" className="quiet" disabled={busy} onClick={() => void close()}>
                Mark as dealt with
              </button>
            </div>
          </form>
        ) : null}

        <p className="muted small">
          Everything here is kept. {user.side === 'pharmacy' ? 'The home' : 'The pharmacy'} sees it exactly as written.
        </p>
      </div>
    </Overlay>
  );
}

function StartDialog({
  homeId,
  onClose,
  onStarted,
}: {
  homeId: string;
  onClose: () => void;
  onStarted: (id: string) => void;
}) {
  const [residents, setResidents] = useState<Resident[]>([]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [aboutId, setAboutId] = useState('');
  const [urgent, setUrgent] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<{ residents: Resident[] }>(`/api/homes/${homeId}/residents`)
      .then((data) => setResidents(data.residents))
      .catch(() => setResidents([]));
  }, [homeId]);

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setBusy(true);
      setError('');
      try {
        const data = await api.post<{ id: string }>(`/api/homes/${homeId}/threads`, {
          subject,
          message,
          aboutType: aboutId ? 'resident' : 'general',
          aboutId: aboutId || null,
          urgent,
        });
        onStarted(data.id);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That did not send.');
      } finally {
        setBusy(false);
      }
    },
    [homeId, subject, message, aboutId, urgent, onStarted],
  );

  return (
    <Overlay onClose={onClose}>
      <form className="card wide" onSubmit={submit}>
        <h2>Start a message</h2>

        <label htmlFor="msg-about">Who is it about?</label>
        <select id="msg-about" value={aboutId} onChange={(e) => setAboutId(e.target.value)}>
          <option value="">Nobody in particular</option>
          {residents.map((resident) => (
            <option key={resident.id} value={resident.id}>
              {resident.title ? `${resident.title} ` : ''}{resident.firstName} {resident.lastName}
              {resident.room ? ` · Room ${resident.room}` : ''}
            </option>
          ))}
        </select>

        <label htmlFor="msg-subject">What is it about?</label>
        <input id="msg-subject" required minLength={3} value={subject} onChange={(e) => setSubject(e.target.value)}
          placeholder="Amlodipine not in this cycle" />

        <label htmlFor="msg-body">Your message</label>
        <textarea id="msg-body" rows={4} required value={message} onChange={(e) => setMessage(e.target.value)} />

        <label className="check">
          <input type="checkbox" checked={urgent} onChange={(e) => setUrgent(e.target.checked)} />
          <span>Mark it urgent — it goes to the top of the other side's list</span>
        </label>
        <p className="muted small">
          If somebody is going to miss a dose because of this, ring as well. A message is not an alarm.
        </p>

        {error ? <p className="error" role="alert">{error}</p> : null}

        <div className="row-actions">
          <button type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send it'}</button>
          <button type="button" className="quiet" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Overlay>
  );
}

function formatWhen(iso: string): string {
  const when = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  const today = new Date();
  const sameDay = when.toDateString() === today.toDateString();
  const time = `${pad(when.getHours())}:${pad(when.getMinutes())}`;
  return sameDay ? time : `${pad(when.getDate())}/${pad(when.getMonth() + 1)} ${time}`;
}
